"use client";
import useTranslate from "@app/_hooks/useTranslate";

import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import type { z } from "zod";

import { cn } from "@/lib/utils";
import { findMarketByLocale } from "@features/market/config/market-config";

import { Heading } from "../ui";
import VoyadoFormInputField from "./VoyadoFormInputField";
import VoyadoFormSubmitButton from "./VoyadoFormSubmitButton";
import { useCreateVoyadoContact } from "./VoyadoSignUpForm.hooks";
import { voyadoContactSchema } from "./VoyadoSignUpForm.schema";
import VoyadoSignupFormSuccess from "./VoyadoSignupFormSuccess";

export default function VoyadoSignUpForm({
  className,
  locale,
  reference,
}: {
  className?: string;
  locale?: string;
  reference?: string | null;
}) {
  const t = useTranslate();
  const market = findMarketByLocale(locale ?? "");
  const methods = useForm<
    z.input<typeof voyadoContactSchema>,
    any,
    z.output<typeof voyadoContactSchema>
  >({
    resolver: zodResolver(voyadoContactSchema as any),
    defaultValues: {
      countryCode: market.countryCode,
    },
  });
  const {
    mutate: createVoyadoContactMutation,
    isSuccess,
    isPending,
    isError,
  } = useCreateVoyadoContact({
    label: reference ?? "voyado-sign-up-form",
  });

  const onSubmit = methods.handleSubmit((data) => {
    createVoyadoContactMutation({
      locale: market.locale,
      contact: {
        ...data,
        countryCode: data.countryCode ?? market.countryCode,
        externalStoreId: market.externalStoreId,
        source: reference ?? "voyado-sign-up-form",
      },
    });
  });

  // If the user is already signed up we get an error with "Conflict" back from the CRM service.
  // We handle this error by a success message to the user, which is better than nothing happens / showing an error message.
  if (isSuccess || isError) {
    return <VoyadoSignupFormSuccess />;
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={onSubmit}
        className={cn("flex flex-col gap-2 bg-white p-4 text-black", className)}
      >
        {t("voyadoSignUpFormHeading", { strict: true }) && (
          <Heading
            className="lg:mb-3"
            size={{
              initial: 5,
              lg: 4,
            }}
          >
            {t("voyadoSignUpFormHeading")}
          </Heading>
        )}
        <div className="grid grid-cols-2 gap-2">
          <VoyadoFormInputField
            type="text"
            name="firstName"
            label={t("voyadoSignUpFormFirstNameLabel") ?? "First name"}
            required
          />
          <VoyadoFormInputField
            type="text"
            name="lastName"
            label={t("voyadoSignUpFormLastNameLabel") ?? "Last name"}
            required
          />
        </div>
        <VoyadoFormInputField
          type="email"
          name="email"
          label={t("voyadoSignUpFormEmailLabel") ?? "Email address"}
          required
        />
        <VoyadoFormInputField
          name="mobilePhone"
          type="tel"
          label={t("voyadoSignUpFormPhoneNumberLabel") ?? "Phone number"}
        />
        <VoyadoFormSubmitButton isLoading={isPending}>
          {t("voyadoSignUpFormSubmitButton") ?? "Sign up"}
        </VoyadoFormSubmitButton>
      </form>
    </FormProvider>
  );
}
